import React, { useCallback, useEffect } from "react";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { CUSTOMER_DETAIL } from "../../../actions/admin/customer-actions";
import Loader from "../../../components/common/loader/Loader";
import { formatToLongDate } from "../../../utils/common";
import { formatAddressLine } from "../../../utils/orderAddress";
import {
  formatAddressTypeLabel,
  formatBooleanLabel,
  formatDateTime,
  formatPersonName,
  parseVirtualLockers,
  resolveFormattedDisplayName,
} from "./customerDetailUtils";
import theme from "../../../theme";

const listPath = "/admin/customers";

const cardSx = {
  elevation: 0,
  borderRadius: 2,
  border: "1px solid #e8eaed",
  width: "100%",
};

const fieldLabelSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: theme.palette.custom.profileText,
  lineHeight: 1.3,
  letterSpacing: "0.01em",
};

const fieldValueSx = {
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: theme.palette.custom.lightGrey,
  lineHeight: 1.35,
  mt: 0.375,
};

const contactTextSx = {
  fontSize: "0.875rem",
  fontWeight: 400,
  color: theme.palette.custom.profileText,
  lineHeight: 1.5,
};

const cardContentSx = {
  p: { xs: 2, sm: 2.5 },
  "&:last-child": { pb: { xs: 2, sm: 2.5 } },
};

const fieldIconSx = {
  fontSize: 16,
  color: theme.palette.custom.primary,
  opacity: 0.85,
  flexShrink: 0,
};

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) || "—").toUpperCase();
}

function OutlineBadge({ label, variant = "success" }) {
  const styles = {
    success: {
      color: theme.palette.custom.success,
      border: `1px solid ${theme.palette.custom.success}`,
      bgcolor: "rgba(0, 192, 70, 0.08)",
      dot: theme.palette.custom.success,
    },
    danger: {
      color: theme.palette.custom.danger,
      border: `1px solid ${theme.palette.custom.danger}`,
      bgcolor: "rgba(255, 56, 80, 0.08)",
      dot: theme.palette.custom.danger,
    },
    warning: {
      color: theme.palette.custom.warning,
      border: `1px solid ${theme.palette.custom.warning}`,
      bgcolor: "rgba(255, 133, 0, 0.08)",
      dot: theme.palette.custom.warning,
    },
  };
  const s = styles[variant] ?? styles.warning;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: "20px",
        border: s.border,
        bgcolor: s.bgcolor,
        color: s.color,
        fontSize: "0.8125rem",
        fontWeight: 600,
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: s.dot, flexShrink: 0 }}
      />
      {label}
    </Box>
  );
}

function StatusBadge({ label, variant = "success" }) {
  const palette = {
    success: theme.palette.custom.success,
    danger: theme.palette.custom.danger,
    warning: theme.palette.custom.warning,
  };
  const bg = palette[variant] ?? palette.warning;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.35,
        borderRadius: "20px",
        bgcolor: bg,
        color: theme.palette.custom.white,
        fontSize: "0.75rem",
        fontWeight: 700,
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#fff", flexShrink: 0 }}
      />
      {label}
    </Box>
  );
}

function ProfileField({ label, value, valueColor, monospace = false, icon: Icon }) {
  const display = value == null || value === "" ? "—" : value;
  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {Icon && <Icon sx={fieldIconSx} />}
        <Typography sx={fieldLabelSx}>{label}</Typography>
      </Box>
      <Typography
        sx={{
          ...fieldValueSx,
          pl: Icon ? 3.25 : 0,
          color: valueColor || theme.palette.custom.lightGrey,
          wordBreak: monospace ? "break-all" : "normal",
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

function SectionHeading({ title, icon: Icon, iconBg }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      {Icon && (
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            bgcolor: iconBg || theme.palette.custom.chipsBgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18, color: theme.palette.custom.primary }} />
        </Box>
      )}
      <Typography
        fontWeight={700}
        fontSize="1rem"
        color={theme.palette.custom.lightGrey}
        sx={{ letterSpacing: "-0.01em" }}
      >
        {title}
      </Typography>
    </Box>
  );
}

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { customerDetail, customerDetailLoading, customerDetailError } = useSelector(
    (state) => state?.customer ?? {}
  );

  const fetchDetail = useCallback(() => {
    if (!customerId) return;
    dispatch({ type: CUSTOMER_DETAIL, payload: { id: customerId } });
  }, [dispatch, customerId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const customer = customerDetail;
  const displayName = resolveFormattedDisplayName(customer);
  const firstName = formatPersonName(customer?.first_name);
  const lastName = formatPersonName(customer?.last_name);
  const lockerInfo = parseVirtualLockers(customer?.virtual_lockers);
  const lockers = lockerInfo.lockers;

  const statusLabel = formatBooleanLabel(customer?.is_suspended, {
    trueLabel: "Suspended",
    falseLabel: "Active",
  });
  const statusVariant = customer?.is_suspended ? "danger" : "success";
  const statusColor = customer?.is_suspended
    ? theme.palette.custom.danger
    : theme.palette.custom.success;

  const verifyLabel = formatBooleanLabel(customer?.is_email_verified, {
    trueLabel: "Verified",
    falseLabel: "Not verified",
  });
  const verifyVariant = customer?.is_email_verified ? "success" : "danger";
  const verifyColor = customer?.is_email_verified
    ? theme.palette.custom.success
    : theme.palette.custom.danger;

  const memberSince = formatDateTime(customer?.createdAt);
  const addresses = Array.isArray(customer?.addresses) ? customer.addresses : [];

  if (customerDetailLoading && !customer) {
    return (
      <Box sx={{ py: 6 }}>
        <Loader minHeight={320} size={48} />
      </Box>
    );
  }

  if (!customer && customerDetailError) {
    return (
      <Box sx={{ p: 3, width: "100%" }}>
        <Button
          startIcon={<ChevronLeftIcon />}
          onClick={() => navigate(listPath)}
          sx={{ color: theme.palette.custom.primary }}
        >
          Back to customers
        </Button>
        <Typography color="error" sx={{ mt: 2, mb: 1 }}>
          Customer not found or could not be loaded.
        </Typography>
        <Typography variant="body2" color={theme.palette.custom.profileText}>
          {customerDetailError}
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={fetchDetail}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!customer) {
    return null;
  }

  const primaryLockerId = lockers[0]?.locker_id;

  return (
    <Box sx={{ width: "100%", pb: 5, pt: 2, px: { xs: 2, sm: 3 } }}>
      <Button
        size="small"
        startIcon={<ChevronLeftIcon />}
        onClick={() => navigate(listPath)}
        sx={{ mb: 2.5, color: theme.palette.custom.primary, textTransform: "none" }}
      >
        Back to customers
      </Button>

      <Card elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
        <CardContent sx={cardContentSx}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 2.5, sm: 3.5 } }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: theme.palette.custom.chipsBgColor,
                color: theme.palette.custom.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "1.25rem",
                flexShrink: 0,
              }}
            >
              {getInitials(displayName)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                fontWeight={700}
                color={theme.palette.custom.lightGrey}
                sx={{ fontSize: "1.5rem", lineHeight: 1.25, letterSpacing: "-0.02em" }}
              >
                {displayName}
              </Typography>
              {customer.email && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}>
                  <EmailOutlinedIcon
                    sx={{ fontSize: 17, color: theme.palette.custom.profileText, opacity: 0.85 }}
                  />
                  <Typography sx={contactTextSx}>{customer.email}</Typography>
                </Box>
              )}
              {customer.phone_number && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                  <PhoneOutlinedIcon
                    sx={{ fontSize: 17, color: theme.palette.custom.profileText, opacity: 0.85 }}
                  />
                  <Typography sx={contactTextSx}>{customer.phone_number}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 1.75 }}>
                <OutlineBadge label={statusLabel} variant={statusVariant} />
                <OutlineBadge label={verifyLabel} variant={verifyVariant} />
                {lockerInfo.isUnassigned ? (
                  <OutlineBadge label={lockerInfo.label} variant="warning" />
                ) : (
                  primaryLockerId && <OutlineBadge label={primaryLockerId} variant="success" />
                )}
              </Box>
              {customer.createdAt && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.75 }}>
                  <CalendarTodayOutlinedIcon
                    sx={{ fontSize: 17, color: theme.palette.custom.profileText, opacity: 0.85 }}
                  />
                  <Typography sx={contactTextSx}>
                    Member since{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: theme.palette.custom.lightGrey }}
                    >
                      {memberSince}
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Card elevation={0} sx={cardSx}>
              <CardContent sx={cardContentSx}>
                <SectionHeading title="Customer profile" icon={PersonOutlineIcon} />
                <Grid container spacing={{ xs: 2, sm: 3 }} rowSpacing={0} alignItems="flex-start">
                  <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: { sm: 1.5 } }}>
                    <ProfileField label="First name" value={firstName} icon={PersonOutlineIcon} />
                    <ProfileField label="Last name" value={lastName} icon={PersonOutlineIcon} />
                    <ProfileField
                      label="Customer ID"
                      value={customer.id}
                      monospace
                      icon={BadgeOutlinedIcon}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }} sx={{ pl: { sm: 1.5 } }}>
                    <ProfileField
                      label="Email verified"
                      value={verifyLabel}
                      valueColor={verifyColor}
                      icon={MarkEmailReadOutlinedIcon}
                    />
                    <ProfileField
                      label="Account status"
                      value={statusLabel}
                      valueColor={statusColor}
                      icon={CheckCircleOutlineIcon}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={cardSx}>
              <CardContent sx={cardContentSx}>
                <SectionHeading title="Virtual locker" icon={LockOutlinedIcon} />
                {lockerInfo.isUnassigned ? (
                  <>
                    <StatusBadge label={lockerInfo.label} variant="warning" />
                    <Typography sx={{ ...contactTextSx, mt: 1.5 }}>
                      No locker assigned yet.
                    </Typography>
                  </>
                ) : (
                  lockers.map((locker) => (
                    <Box
                      key={locker.id ?? locker.locker_id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: lockers.length > 1 ? 1.5 : 0,
                      }}
                    >
                      <Inventory2OutlinedIcon sx={{ ...fieldIconSx, mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ ...fieldValueSx, mt: 0 }}>{locker.locker_id}</Typography>
                        {locker.locker_expiry_date && (
                          <Typography sx={{ ...fieldLabelSx, mt: 0.5, fontWeight: 500 }}>
                            Expires {formatToLongDate(locker.locker_expiry_date)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card elevation={0} sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeading title="Account summary" icon={SummarizeOutlinedIcon} />
              <ProfileField
                label="Display name"
                value={displayName}
                icon={PersonOutlineIcon}
              />
              <ProfileField label="Email" value={customer.email} icon={EmailOutlinedIcon} />
              <ProfileField
                label="Phone"
                value={customer.phone_number}
                icon={PhoneOutlinedIcon}
              />
              <ProfileField
                label="Member since"
                value={memberSince}
                icon={CalendarTodayOutlinedIcon}
              />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ ...cardSx, mt: 2.5 }}>
            <CardContent sx={cardContentSx}>
              <SectionHeading title="Delivery addresses" icon={HomeOutlinedIcon} />
              {addresses.length === 0 ? (
                <Typography sx={contactTextSx}>No address on file.</Typography>
              ) : (
                addresses.map((addr) => (
                  <Box
                    key={addr.id ?? `${addr.street_address}-${addr.zip_code}`}
                    sx={{
                      mb: addresses.length > 1 ? 2 : 0,
                      pb: addresses.length > 1 ? 2 : 0,
                      borderBottom:
                        addresses.length > 1 ? "1px solid #e8eaed" : "none",
                      "&:last-child": { mb: 0, pb: 0, borderBottom: "none" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      <Typography sx={{ ...fieldValueSx, mt: 0 }}>
                        {formatAddressTypeLabel(addr.address_type)}
                      </Typography>
                      {addr.is_default && (
                        <OutlineBadge label="Default" variant="success" />
                      )}
                    </Box>
                    <Typography sx={contactTextSx}>{formatAddressLine(addr) || "—"}</Typography>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
